import { useState, useEffect } from "react";

/**
 * CookieConsent — GDPR/CCPA compliant consent banner.
 * Blocks GA4 analytics storage until user accepts.
 * Uses gtag Consent Mode v2.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Show after a short delay so it doesn't flash on intro
      const t = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(t);
    }
    if (consent === "accepted") grantConsent();
  }, []);

  const grantConsent = () => {
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
      });
    }
  };

  const handleAccept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    grantConsent();
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem("cookie_consent", "declined");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 99999,
        width: "min(92vw, 640px)",
        background: "rgba(8,12,26,0.96)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(217,119,6,0.25)",
        borderRadius: 16,
        padding: "20px 24px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(217,119,6,0.08)",
        fontFamily: "Inter, system-ui, sans-serif",
        animation: "slideUpCookie 0.5s cubic-bezier(0.16,1,0.3,1) both",
      }}
    >
      <style>{`
        @keyframes slideUpCookie {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Gold top line */}
      <div style={{
        position: "absolute", top: 0, left: "10%", right: "10%", height: 1,
        background: "linear-gradient(90deg,transparent,rgba(217,119,6,0.6),transparent)",
        borderRadius: 1,
      }} />

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>🍪</span>
        <div style={{ flex: 1 }}>
          <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#FDFBF7" }}>
            We use cookies &amp; analytics
          </p>
          <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
            We use Google Analytics and session recording to improve your experience.
            Your data is never sold. See our{" "}
            <a href="/privacy-policy" style={{ color: "#D97706", textDecoration: "underline" }}>
              Privacy Policy
            </a>.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
        <button
          onClick={handleDecline}
          style={{
            padding: "8px 18px", borderRadius: 40, fontSize: 12, fontWeight: 600,
            color: "rgba(255,255,255,0.5)", background: "transparent",
            border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer",
            transition: "border-color 0.2s, color 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
        >
          Decline
        </button>
        <button
          onClick={handleAccept}
          style={{
            padding: "8px 20px", borderRadius: 40, fontSize: 12, fontWeight: 700,
            color: "#0A0F1C", background: "linear-gradient(135deg,#D97706,#FBBF24)",
            border: "none", cursor: "pointer",
            boxShadow: "0 4px 16px rgba(217,119,6,0.3)",
            transition: "box-shadow 0.2s, transform 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(217,119,6,0.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(217,119,6,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
        >
          Accept All
        </button>
      </div>
    </div>
  );
}
